/**
 * Platform Tenant Detail Page
 * Shows overview, usage stats, features, limits, branding, and admin users for one tenant.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  Users,
  MapPin,
  Calendar,
  Shield,
  Activity,
  FileText,
  Route,
  AlertTriangle,
  Clock,
  Pause,
  Play,
  Save,
  UserCog,
  UserX,
  UserCheck,
  Loader2,
  ExternalLink,
  Trash2,
  RefreshCw,
  Database,
} from 'lucide-react';

const FEATURE_LABELS = {
  command_centre: { label: 'Command Centre', icon: Activity },
  patrols: { label: 'Patrols', icon: Route },
  incidents: { label: 'Incidents', icon: AlertTriangle },
  iris_analytics: { label: 'IRIS Analytics', icon: FileText },
  api_access: { label: 'API Access', icon: Shield },
  shift_assurance: { label: 'Shift Assurance', icon: Clock },
  roster_v2: { label: 'Roster V2', icon: Calendar },
};

const DEFAULT_FEATURES = {
  command_centre: true,
  patrols: true,
  incidents: true,
  iris_analytics: true,
  api_access: false,
  shift_assurance: true,
  roster_v2: true,
};

export default function PlatformTenantDetailPage() {
  const { tenantId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(null);
  const [stats, setStats] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState({});
  const [limits, setLimits] = useState({});
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [togglingUser, setTogglingUser] = useState(null);

  // V4.0E-2J-2 — Platform Admin Test Data Management
  const [emergencyRecords, setEmergencyRecords] = useState([]);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [selectedEmergencyIds, setSelectedEmergencyIds] = useState([]);
  const [deletingEmergency, setDeletingEmergency] = useState(false);
  const [purgingEmergency, setPurgingEmergency] = useState(false);


  const fetchAll = useCallback(async () => {
    try {
      const [tenantRes, statsRes, adminsRes, usersRes] = await Promise.all([
        api.get(`/platform/tenants/${tenantId}`),
        api.get(`/platform/tenants/${tenantId}/stats`),
        api.get(`/platform/tenants/${tenantId}/admins`),
        api.get(`/platform/tenants/${tenantId}/users`),
      ]);
      setTenant(tenantRes.data);
      setStats(statsRes.data);
      setAdmins(adminsRes.data);
      setAllUsers(usersRes.data);
      setFeatures({ ...DEFAULT_FEATURES, ...tenantRes.data.features });
      setLimits(tenantRes.data.limits || { max_users: 50, max_sites: 20 });
    } catch (err) {
      toast.error('Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  }, [tenantId, api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchEmergencyRecords = useCallback(async () => {
    setEmergencyLoading(true);
    try {
      const response = await api.get(`/platform/tenants/${tenantId}/emergency-test-data`);
      setEmergencyRecords(response.data?.alerts || []);
      setSelectedEmergencyIds([]);
    } catch (err) {
      console.error('Failed to load emergency test data', err);
      toast.error(err.response?.data?.detail || 'Failed to load emergency test data');
    } finally {
      setEmergencyLoading(false);
    }
  }, [api, tenantId]);

  useEffect(() => {
    fetchEmergencyRecords();
  }, [fetchEmergencyRecords]);


  const handleSaveFeatures = async () => {
    setSavingFeatures(true);
    try {
      await api.put(`/platform/tenants/${tenantId}/features`, { features });
      toast.success('Features updated');
    } catch (err) {
      toast.error('Failed to update features');
    } finally {
      setSavingFeatures(false);
    }
  };

  const handleSaveLimits = async () => {
    setSavingLimits(true);
    try {
      await api.put(`/platform/tenants/${tenantId}/limits`, { limits });
      toast.success('Limits updated');
    } catch (err) {
      toast.error('Failed to update limits');
    } finally {
      setSavingLimits(false);
    }
  };

  const handleSuspend = async () => {
    if (!window.confirm(`Suspend "${tenant.name}"? All users will lose access.`)) return;
    try {
      await api.post(`/platform/tenants/${tenantId}/suspend`);
      toast.success('Tenant suspended');
      fetchAll();
    } catch (err) {
      toast.error('Failed to suspend');
    }
  };

  const handleActivate = async () => {
    try {
      await api.post(`/platform/tenants/${tenantId}/activate`);
      toast.success('Tenant activated');
      fetchAll();
    } catch (err) {
      toast.error('Failed to activate');
    }
  };

  const handleUpdatePlan = async (newPlan) => {
    try {
      await api.put(`/platform/tenants/${tenantId}`, { plan: newPlan });
      toast.success(`Plan updated to ${newPlan}`);
      fetchAll();
    } catch (err) {
      toast.error('Failed to update plan');
    }
  };

  const handleToggleUserStatus = async (user) => {
    const newStatus = user.is_active ? 'inactive' : 'active';
    const action = user.is_active ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${action} ${user.name || user.email}?`)) return;

    setTogglingUser(user.id);
    try {
      await api.patch(`/platform/tenants/${tenantId}/users/${user.id}/status`, {
        status: newStatus,
      });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action.toLowerCase()} user`);
    } finally {
      setTogglingUser(null);
    }
  };

  const toggleEmergencySelection = (id) => {
    setSelectedEmergencyIds((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id]
    );
  };

  const selectAllEmergencies = () => {
    if (emergencyRecords.length > 0 && selectedEmergencyIds.length === emergencyRecords.length) {
      setSelectedEmergencyIds([]);
      return;
    }
    setSelectedEmergencyIds(emergencyRecords.map((item) => item.id));
  };

  const handleDeleteSelectedEmergencies = async () => {
    if (!selectedEmergencyIds.length) {
      toast.error('Select at least one emergency record');
      return;
    }

    const confirmation = window.prompt(
      `You are permanently deleting ${selectedEmergencyIds.length} emergency record(s).\n\n` +
      'Type DELETE TEST DATA to continue.'
    );

    if (confirmation !== 'DELETE TEST DATA') {
      if (confirmation !== null) toast.error('Deletion cancelled — confirmation did not match');
      return;
    }

    setDeletingEmergency(true);
    try {
      const response = await api.post(
        `/platform/tenants/${tenantId}/emergency-test-data/delete`,
        { alert_ids: selectedEmergencyIds, confirmation }
      );
      toast.success(`${response.data?.deleted_count || 0} emergency record(s) deleted`);
      await fetchEmergencyRecords();
    } catch (err) {
      console.error('Emergency record deletion failed', err);
      toast.error(err.response?.data?.detail || 'Failed to delete selected emergency records');
    } finally {
      setDeletingEmergency(false);
    }
  };

  const handlePurgeEmergencyRecords = async () => {
    if (!emergencyRecords.length) {
      toast.error('There are no emergency records to purge');
      return;
    }

    const firstConfirmation = window.confirm(
      `DANGER: This will permanently purge ALL ${emergencyRecords.length} emergency records currently returned for "${tenant?.name || tenantId}".\n\nThis cannot be undone.\n\nContinue?`
    );
    if (!firstConfirmation) return;

    const confirmation = window.prompt(
      'FINAL CONFIRMATION\n\nType DELETE TEST DATA exactly to permanently purge these records.'
    );

    if (confirmation !== 'DELETE TEST DATA') {
      if (confirmation !== null) toast.error('Purge cancelled — confirmation did not match');
      return;
    }

    setPurgingEmergency(true);
    try {
      const response = await api.post(
        `/platform/tenants/${tenantId}/emergency-test-data/purge`,
        { tenant_id: tenant?.id || tenantId, confirmation }
      );
      toast.success(`${response.data?.deleted_count || 0} emergency record(s) purged`);
      await fetchEmergencyRecords();
      await fetchAll();
    } catch (err) {
      console.error('Emergency purge failed', err);
      toast.error(err.response?.data?.detail || 'Failed to purge emergency records');
    } finally {
      setPurgingEmergency(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-20 text-zinc-500">
        Tenant not found.
        <Button variant="link" onClick={() => navigate('/platform')} className="text-amber-400 ml-2">
          Back to Tenants
        </Button>
      </div>
    );
  }

  const statusColor = tenant.status === 'active'
    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
    : 'bg-red-500/15 text-red-400 border-red-500/30';

  return (
    <div data-testid="platform-tenant-detail" className="max-w-5xl mx-auto space-y-6">
      {/* Back + Header */}
      <div>
        <button
          onClick={() => navigate('/platform')}
          className="flex items-center gap-1 text-xs text-zinc-500 hover:text-amber-400 transition-colors mb-3"
          data-testid="back-to-tenants"
        >
          <ArrowLeft className="w-3 h-3" /> Back to Tenants
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{tenant.name}</h1>
              <p className="text-sm text-zinc-500 font-mono">
  {tenant.domain || `${tenant.slug}.rapidxtech.com.au`}
</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded border font-mono ${statusColor}`}>
              {tenant.status}
            </span>
            {tenant.status === 'active' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSuspend}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                data-testid="suspend-tenant-btn"
              >
                <Pause className="w-3 h-3 mr-1" /> Suspend
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleActivate}
                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                data-testid="activate-tenant-btn"
              >
                <Play className="w-3 h-3 mr-1" /> Activate
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Users" value={stats.users?.total || 0} sub={`${stats.users?.active || 0} active`} />
          <StatCard icon={MapPin} label="Sites" value={stats.sites?.total || 0} />
          <StatCard icon={Calendar} label="Shifts" value={stats.shifts?.total || 0} sub={`${stats.shifts?.last_30_days || 0} in 30d`} />
          <StatCard icon={AlertTriangle} label="Incidents" value={stats.incidents?.total || 0} sub={`${stats.incidents?.open || 0} open`} />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Plan */}
        <Section title="Plan">
          <div className="flex items-center gap-3">
            {['starter', 'standard', 'professional', 'enterprise'].map(p => (
              <button
                key={p}
                onClick={() => handleUpdatePlan(p)}
                className={`px-3 py-1.5 rounded-md text-xs font-mono transition-colors ${
                  tenant.plan === p
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    : 'bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:text-zinc-300 hover:border-zinc-600'
                }`}
                data-testid={`plan-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        </Section>

        {/* Info */}
        <Section title="Details">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <InfoRow label="Timezone" value={tenant.timezone || 'Australia/Sydney'} />
            <InfoRow label="Country" value={tenant.country || 'AU'} />
            <InfoRow label="Created" value={tenant.created_at ? new Date(tenant.created_at).toLocaleDateString('en-AU') : '—'} />
            <InfoRow label="Updated" value={tenant.updated_at ? new Date(tenant.updated_at).toLocaleDateString('en-AU') : '—'} />
          </div>
        </Section>

        {/* Features */}
        <Section
          title="Features"
          action={
            <Button
              size="sm"
              onClick={handleSaveFeatures}
              disabled={savingFeatures}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7"
              data-testid="save-features"
            >
              <Save className="w-3 h-3 mr-1" /> {savingFeatures ? 'Saving...' : 'Save'}
            </Button>
          }
        >
          <div className="space-y-2">
            {Object.entries(FEATURE_LABELS).map(([key, { label, icon: Icon }]) => (
              <div key={key} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 text-zinc-500" />
                  <span className="text-sm text-zinc-300">{label}</span>
                </div>
                <Switch
                  checked={features[key] || false}
                  onCheckedChange={(v) => setFeatures({ ...features, [key]: v })}
                  data-testid={`feature-${key}`}
                />
              </div>
            ))}
          </div>
        </Section>

        {/* Limits */}
        <Section
          title="Limits"
          action={
            <Button
              size="sm"
              onClick={handleSaveLimits}
              disabled={savingLimits}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7"
              data-testid="save-limits"
            >
              <Save className="w-3 h-3 mr-1" /> {savingLimits ? 'Saving...' : 'Save'}
            </Button>
          }
        >
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-400 text-xs">Max Users</Label>
              <Input
                type="number"
                value={limits.max_users || ''}
                onChange={e => setLimits({ ...limits, max_users: parseInt(e.target.value) || 0 })}
                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                data-testid="limit-max-users"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Max Sites</Label>
              <Input
                type="number"
                value={limits.max_sites || ''}
                onChange={e => setLimits({ ...limits, max_sites: parseInt(e.target.value) || 0 })}
                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                data-testid="limit-max-sites"
              />
            </div>
          </div>
        </Section>
      </div>

      {/* V4.0E-2J-2 — Platform Admin Test Data Management */}
      <div data-testid="platform-emergency-test-data">
        <Section
          title="Test Data Management — Emergency Records"
          fullWidth
          icon={Database}
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={fetchEmergencyRecords}
              disabled={emergencyLoading}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${emergencyLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          }
        >
          <div className="rounded-lg border border-red-900/40 bg-red-950/10 p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-300">Platform Super Admin — Destructive Data Management</p>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
                  Permanently remove test emergency/panic records from this tenant. Deletion activity is recorded separately in the Platform Audit Log.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="border-zinc-700 text-zinc-400">{emergencyRecords.length} records</Badge>
              {selectedEmergencyIds.length > 0 && (
                <Badge variant="outline" className="border-amber-500/30 text-amber-400">{selectedEmergencyIds.length} selected</Badge>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={selectAllEmergencies} disabled={!emergencyRecords.length} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs">
                {selectedEmergencyIds.length === emergencyRecords.length && emergencyRecords.length > 0 ? 'Clear Selection' : 'Select All'}
              </Button>

              <Button variant="outline" size="sm" onClick={handleDeleteSelectedEmergencies} disabled={deletingEmergency || purgingEmergency || !selectedEmergencyIds.length} className="border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs">
                {deletingEmergency ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Delete Selected
              </Button>

              <Button size="sm" onClick={handlePurgeEmergencyRecords} disabled={deletingEmergency || purgingEmergency || !emergencyRecords.length} className="bg-red-700 hover:bg-red-800 text-white text-xs">
                {purgingEmergency ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                Purge Emergency Data
              </Button>
            </div>
          </div>

          {emergencyLoading ? (
            <div className="flex items-center justify-center py-10 text-zinc-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading emergency records...
            </div>
          ) : emergencyRecords.length === 0 ? (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/30 py-10 text-center">
              <Shield className="w-8 h-8 text-emerald-500/60 mx-auto mb-2" />
              <p className="text-sm text-zinc-300">No emergency records found</p>
            </div>
          ) : (
            <div className="border border-zinc-800 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-zinc-900/80">
                    <tr className="border-b border-zinc-800">
                      <th className="w-10 px-3 py-2"><input type="checkbox" checked={emergencyRecords.length > 0 && selectedEmergencyIds.length === emergencyRecords.length} onChange={selectAllEmergencies} className="accent-amber-500" aria-label="Select all emergency records" /></th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Officer</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Site</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Status</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Created</th>
                      <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500">Record ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emergencyRecords.map((item) => {
                      const selected = selectedEmergencyIds.includes(item.id);
                      return (
                        <tr key={item.id} className={`border-b border-zinc-800/70 last:border-b-0 ${selected ? 'bg-amber-500/5' : 'bg-[#0c0c10] hover:bg-zinc-900/60'}`}>
                          <td className="px-3 py-3"><input type="checkbox" checked={selected} onChange={() => toggleEmergencySelection(item.id)} className="accent-amber-500" /></td>
                          <td className="px-3 py-3"><p className="text-xs font-medium text-zinc-200">{item.officer_name || 'Unknown'}</p>{item.is_test && <span className="text-[9px] uppercase font-mono text-amber-500">Test</span>}</td>
                          <td className="px-3 py-3 text-xs text-zinc-400">{item.site_name || '—'}</td>
                          <td className="px-3 py-3"><Badge variant="outline" className={`text-[10px] ${item.status === 'resolved' ? 'border-emerald-500/30 text-emerald-400' : item.status === 'active' ? 'border-red-500/30 text-red-400' : 'border-amber-500/30 text-amber-400'}`}>{(item.status || 'unknown').toUpperCase()}</Badge></td>
                          <td className="px-3 py-3 text-xs text-zinc-500 whitespace-nowrap">{item.created_at ? new Date(item.created_at).toLocaleString('en-AU') : '—'}</td>
                          <td className="px-3 py-3"><span className="text-[10px] font-mono text-zinc-600" title={item.id}>{item.id?.slice(-10)}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2 text-[10px] text-zinc-600">
            <Shield className="w-3 h-3" /> Permanent deletion requires <span className="font-mono text-red-400">DELETE TEST DATA</span>
          </div>
        </Section>
      </div>

      {/* Users */}
      <Section title="Users" fullWidth icon={UserCog}>
        <div className="space-y-1.5">
          {allUsers.length === 0 && (
            <p className="text-sm text-zinc-500 py-2">No users found.</p>
          )}
          {allUsers.map(u => {
            const isActive = u.is_active !== false;
            const isToggling = togglingUser === u.id;
            return (
              <div
                key={u.id}
                className={`flex items-center justify-between py-2.5 px-3 rounded-md border transition-colors ${
                  isActive
                    ? 'bg-zinc-900/40 border-zinc-800/40'
                    : 'bg-red-950/20 border-red-900/30'
                }`}
                data-testid={`user-row-${u.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${isActive ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <div className="min-w-0">
                    <p className={`text-sm truncate ${isActive ? 'text-white' : 'text-zinc-500'}`}>
                      {u.name || u.email}
                    </p>
                    <p className="text-xs text-zinc-600 truncate">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="text-[10px] font-mono text-amber-500/70 uppercase hidden sm:inline">
                    {u.role?.replace('_', ' ')}
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-1.5 py-0 h-5 ${
                      isActive
                        ? 'border-emerald-500/30 text-emerald-400'
                        : 'border-red-500/30 text-red-400'
                    }`}
                    data-testid={`user-status-${u.id}`}
                  >
                    {isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isToggling}
                    onClick={() => handleToggleUserStatus(u)}
                    className={`h-7 px-2 text-xs ${
                      isActive
                        ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10'
                        : 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10'
                    }`}
                    data-testid={`toggle-user-${u.id}`}
                  >
                    {isToggling ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : isActive ? (
                      <><UserX className="w-3 h-3 mr-1" /> Deactivate</>
                    ) : (
                      <><UserCheck className="w-3 h-3 mr-1" /> Activate</>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[#111114] border border-zinc-800/60 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-zinc-500" />
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold text-white">{value}</p>
      {sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
}

function Section({ title, children, action, fullWidth, icon: Icon }) {
  return (
    <div className={`bg-[#111114] border border-zinc-800/60 rounded-lg p-4 ${fullWidth ? 'lg:col-span-2' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-zinc-500" />}
          <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <span className="text-[10px] text-zinc-600 uppercase">{label}</span>
      <p className="text-zinc-300 text-xs">{value}</p>
    </div>
  );
}
